import styled from "@emotion/styled";
import classNames from "classnames";
import { get } from "es-toolkit/compat";
import { useMemo, type ReactNode } from "react";

import { directionBiased, useDragOperation, useSortable, type Data, type UniqueIdentifier } from "@skilitics/drag-drop";

import { SkiliticsIcon } from "../icons/Icon";

// DEV: debug code to show drag/drop containers in the browser.
export const showDndDebugInfo = false;

export const OrderableItemContainer = styled.div`
  position: relative;
  max-width: 100%;
  flex: 1;

  &.is-dragging {
    z-index: 10;
  }

  &.drag-active:not(.is-dragging) > * {
    pointer-events: none;
  }

  &.over-allowed.above {
    border-top: solid 2px ${({ theme }) => theme.primary};
  }

  &.over-allowed.below {
    border-bottom: solid 2px ${({ theme }) => theme.primary};
  }
`;

const DragItemIconHandle = styled.div<{ $width: string }>`
  flex: 0 0 ${({ $width }) => $width};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: move;
  color: ${({ theme }) => theme.generalTextIcon};
`;

const StyledDragItem = styled.div<{ $dragSize: number }>`
  position: relative;
  display: grid;
  align-items: center;
  max-width: 100%;
  grid-column-gap: 5px;

  box-shadow: none;
  outline: none;
  box-sizing: border-box;
  list-style: none;
  transform-origin: 50% 50%;

  -webkit-tap-highlight-color: transparent;

  transition: box-shadow 200ms cubic-bezier(0.18, 0.67, 0.6, 1.22);
  touch-action: manipulation;
  cursor: grab;

  &.handle {
    border-radius: ${({ theme }) => theme.borderRadius}px;
    background: ${({ theme }) => theme.surface};
    grid-template-columns: ${({ $dragSize }) => $dragSize * 1.2}px auto;
  }

  &.dragging {
    opacity: 0.5;
    z-index: 0;
  }
`;

export type OrderableItemProps = {
  id: UniqueIdentifier;
  index: number;
  handle?: boolean;
  dragSize?: number;
  disabled?: boolean;
  dragActive?: boolean;
  data?: Data;
  children: ReactNode;
  accepts?: Record<string, string[]>;
};

export const OrderableItem = ({
  id,
  index,
  data,
  dragSize = 14,
  children,
  disabled,
  handle,
  accepts,
  dragActive,
}: OrderableItemProps) => {
  const { ref, handleRef, isDragging, isDropTarget, sortable } = useSortable({
    id,
    index,
    data,
    disabled,
    collisionDetector: directionBiased,
  });

  const { source } = useDragOperation();

  const allowedOver = useMemo(() => {
    if (!disabled && accepts) {
      for (const key of Object.keys(accepts)) {
        const activeData = get(source?.data, key);
        if (activeData && (get(accepts, key) ?? []).includes(activeData)) {
          return true;
        }
      }
    }
    return false;
  }, [disabled, accepts, source?.data]);

  const aboveOrBelow =
    source && "index" in source && typeof source.index === "number"
      ? source.index < sortable.index
        ? "below"
        : "above"
      : "below";

  return (
    <OrderableItemContainer
      ref={ref}
      className={classNames({
        "is-dragging": isDragging,
        "drag-active": dragActive,
        "over-allowed": allowedOver && isDropTarget,
        "not-over-allowed": allowedOver && !isDropTarget,
        "not-allowed": !allowedOver,
        [aboveOrBelow]: allowedOver,
      })}
      data-test="item"
    >
      <StyledDragItem
        className={classNames({ dragging: isDragging, handle })}
        $dragSize={dragSize}
        data-test="draggable-item"
      >
        {handle ? (
          <DragItemIconHandle ref={handleRef} $width={`${dragSize}px`} data-test="drag-handle">
            <SkiliticsIcon icon="drag-grip" size={dragSize} />
          </DragItemIconHandle>
        ) : null}
        {children}
      </StyledDragItem>
    </OrderableItemContainer>
  );
};
